import { Component, signal, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EventosService, EventoEnVivo } from '../services/eventos.service';
import { NotificationService } from '../services/notification.service';

interface ChatMessage {
  id: number;
  user: string;
  avatar: string;
  message: string;
  time: string;
  isInstructor?: boolean;
}

interface Participant {
  id: number;
  name: string;
  avatar: string;
  role: 'instructor' | 'student';
}

@Component({
  selector: 'app-live-class',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './live-class.html',
  styleUrl: './live-class.css',
})
export class LiveClassComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private sanitizer = inject(DomSanitizer);
  private eventosService = inject(EventosService);
  private notificationService = inject(NotificationService);

  currentTab = signal<'chat' | 'participants' | 'qa'>('chat');
  newMessage = signal('');
  isLoading = signal(false);
  evento = signal<EventoEnVivo | null>(null);
  inscrito = signal(false);
  
  safeVideoIframe: SafeHtml | null = null;
  safeChatIframe: SafeHtml | null = null;

  chatMessages: ChatMessage[] = [];
  participants: Participant[] = [];

  ngOnInit() {
    window.scrollTo(0, 0);
    
    // Obtener ID del evento de la ruta
    this.route.params.subscribe(params => {
      const eventoId = +params['id'];
      if (eventoId) {
        this.cargarEvento(eventoId);
      }
    });
  }

  /**
   * Carga los datos del evento desde el backend
   */
  cargarEvento(idEvento: number) {
    this.isLoading.set(true);

    this.eventosService.getEvento(idEvento).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        
        console.log('📺 Respuesta del servidor:', response);
        
        // El backend puede responder con { data } directamente o con { ok, data }
        if (response.data) {
          console.log('📋 Datos del evento:', response.data);
          this.evento.set(response.data);
          
          // Sanitizar los iframes
          if (response.data.iframe_video) {
            console.log('🎥 iframe_video recibido:', response.data.iframe_video);
            // Limpiar estilos inline del iframe para que use 100% del ancho
            let cleanedVideoIframe = this.cleanIframeStyles(response.data.iframe_video);
            this.safeVideoIframe = this.sanitizer.bypassSecurityTrustHtml(cleanedVideoIframe);
            console.log('✅ Video iframe sanitizado y limpiado');
          } else {
            console.warn('⚠️ No hay iframe_video en la respuesta');
          }
          
          if (response.data.iframe_chat) {
            console.log('💬 iframe_chat recibido:', response.data.iframe_chat);
            // Limpiar estilos inline del iframe para que use 100% del ancho
            let cleanedChatIframe = this.cleanIframeStyles(response.data.iframe_chat);
            this.safeChatIframe = this.sanitizer.bypassSecurityTrustHtml(cleanedChatIframe);
            console.log('✅ Chat iframe sanitizado y limpiado');
          } else {
            console.warn('⚠️ No hay iframe_chat en la respuesta');
          }
          
          console.log('✅ Evento cargado exitosamente');
          console.log('📺 Estado final - safeVideoIframe:', this.safeVideoIframe ? 'EXISTE' : 'NULL');
          console.log('💬 Estado final - safeChatIframe:', this.safeChatIframe ? 'EXISTE' : 'NULL');
          this.notificationService.success('Evento cargado correctamente');
        } else if (response.ok === false) {
          const errorMsg = response.message || response.msg || 'No se pudo cargar el evento';
          this.notificationService.error('Error', errorMsg);
          this.goBack();
        } else {
          this.notificationService.error('Error', 'Respuesta inesperada del servidor');
          this.goBack();
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('❌ Error al cargar evento:', error);
        this.notificationService.error(
          'Error de Conexión',
          'No se pudo conectar con el servidor'
        );
        this.goBack();
      }
    });
  }

  /**
   * Inscribe al usuario en el evento
   */
  inscribirse() {
    const evento = this.evento();
    if (!evento) return;
    
    this.eventosService.inscribirseEvento(evento.id).subscribe({
      next: (response) => {
        if (response.ok) {
          this.inscrito.set(true);
          this.notificationService.success(
            '¡Inscrito!',
            'Te has inscrito exitosamente al evento'
          );
          
          // Actualizar el contador de inscritos
          const eventoActual = this.evento();
          if (eventoActual) {
            this.evento.set({
              ...eventoActual,
              inscritos: eventoActual.inscritos + 1
            });
          }
        } else {
          this.notificationService.error(
            'Error',
            response.msg || 'No se pudo inscribir'
          );
        }
      },
      error: (error) => {
        console.error('Error al inscribirse:', error);
        this.notificationService.error(
          'Error',
          error.error?.msg || 'No se pudo completar la inscripción'
        );
      }
    });
  }

  ngOnDestroy() {
    // Limpiar recursos si es necesario
  }

  setTab(tab: 'chat' | 'participants' | 'qa') {
    this.currentTab.set(tab);
  }

  sendMessage() {
    if (this.newMessage().trim()) {
      const message: ChatMessage = {
        id: this.chatMessages.length + 1,
        user: 'Tú',
        avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop',
        message: this.newMessage(),
        time: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      };
      this.chatMessages.push(message);
      this.newMessage.set('');
      
      // Auto scroll al final del chat
      setTimeout(() => {
        const chatContainer = document.querySelector('.chat-messages');
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      }, 100);
    }
  }

  goBack() {
    this.router.navigate(['/dashboard/home']);
  }

  /**
   * Limpia los estilos inline del iframe para usar 100% del ancho y alto
   */
  private cleanIframeStyles(iframeHtml: string): string {
    // Remover estilos de dimensiones fijas
    let cleaned = iframeHtml.replace(/width:\s*\d+px/gi, 'width:100%');
    cleaned = cleaned.replace(/max-width:\s*\d+px/gi, 'max-width:100%');
    cleaned = cleaned.replace(/min-width:\s*\d+px/gi, 'min-width:100%');
    cleaned = cleaned.replace(/height:\s*\d+px/gi, 'height:100%');
    cleaned = cleaned.replace(/max-height:\s*\d+px/gi, 'max-height:100%');
    cleaned = cleaned.replace(/min-height:\s*\d+px/gi, 'min-height:100%');
    
    // Si ya tiene atributo style, añadir estilos importantes
    if (cleaned.includes('style=')) {
      cleaned = cleaned.replace(/style\s*=\s*["']([^"']*)["']/i, (match, styles) => {
        // Asegurar que tenga width y height al 100%
        let newStyles = styles;
        if (!styles.includes('width:')) {
          newStyles += ';width:100%';
        }
        if (!styles.includes('height:')) {
          newStyles += ';height:100%';
        }
        if (!styles.includes('border:')) {
          newStyles += ';border:0';
        }
        return `style="${newStyles}"`;
      });
    } else {
      // Si no tiene style, añadirlo
      cleaned = cleaned.replace(/<iframe/i, '<iframe style="width:100%;height:100%;border:0"');
    }
    
    return cleaned;
  }

  /**
   * Verifica si el evento está en vivo ahora
   */
  isEnVivo(): boolean {
    const evento = this.evento();
    return evento ? this.eventosService.isEventoEnVivo(evento) : false;
  }

  /**
   * Formatea la fecha del evento
   */
  getFechaEvento(): string {
    const evento = this.evento();
    return evento ? this.eventosService.formatFechaEvento(evento.fecha_inicio) : '';
  }

  /**
   * Obtiene la duración del evento
   */
  getDuracion(): string {
    const evento = this.evento();
    return evento ? this.eventosService.getDuracionEvento(evento) : '';
  }
}

